// src/auth/AuthPage.tsx
import { useState } from "react";
import {
  signInWithEmailPassword,
  signUpWithEmailPassword,
  signInWithProvider,
  sendPasswordReset,
} from "../services/auth";
import "./auth.css";

type Mode = "signin" | "signup" | "reset";

export default function AuthPage() {
  const [mode, setMode] = useState<Mode>("signin");

  // Campos
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");

  // Estado UI
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const clearAlerts = () => {
    setMsg(null);
    setErr(null);
  };

  const isEmailOk = email.trim().length > 3 && email.includes("@");
  const isPwdOk = password.length >= 6;
  const isPwdMatch = password === password2;
  const isNameOk = fullName.trim().length >= 2;

  const canSignin = isEmailOk && isPwdOk;
  const canSignup = isEmailOk && isPwdOk && isPwdMatch && isNameOk;

  async function handleSignIn() {
    clearAlerts();
    setLoading(true);
    const { error } = await signInWithEmailPassword(email.trim(), password);
    setLoading(false);
    if (error) {
      setErr(error.message || "No se pudo iniciar sesión.");
      return;
    }
    setMsg("Sesión iniciada. Redirigiendo…");
    // AuthGate detectará la sesión y mostrará la app.
  }

  async function handleSignUp() {
    clearAlerts();
    if (!isPwdMatch) {
      setErr("Las contraseñas no coinciden.");
      return;
    }
    setLoading(true);
    const { error } = await signUpWithEmailPassword(email.trim(), password, {
      full_name: fullName.trim(),
    });
    setLoading(false);
    if (error) {
      setErr(error.message || "No se pudo crear la cuenta.");
      return;
    }
    setMsg(
      "Registro completado. Revisa tu correo si requiere confirmación. Ahora puedes iniciar sesión."
    );
    setMode("signin");
    setPassword("");
    setPassword2("");
  }

  async function handleReset() {
    clearAlerts();
    setLoading(true);
    const { error } = await sendPasswordReset(email.trim());
    setLoading(false);
    if (error) {
      setErr(error.message || "No se pudo enviar el correo de restablecimiento.");
      return;
    }
    setMsg("Si el correo existe, recibirás un enlace para restablecer la contraseña.");
    setMode("signin");
  }

  async function handleOAuth(provider: "google" | "github") {
    clearAlerts();
    setLoading(true);
    const { error } = await signInWithProvider(provider);
    setLoading(false);
    if (error) {
      setErr(error.message || "No se pudo iniciar sesión con el proveedor.");
    } else {
      setMsg("Redirigiendo al proveedor…");
    }
  }

  return (
    <div className="auth-page">

      <div className="auth-card">
        <h1 className="auth-title">
          {mode === "signin" && "Iniciar sesión"}
          {mode === "signup" && "Crear cuenta"}
          {mode === "reset" && "Restablecer contraseña"}
        </h1>
        <p className="auth-subtitle">Accede para gestionar tus proyectos y tareas.</p>

        {(msg || err) && (
          <div className={`auth-alert ${err ? "is-error" : "is-ok"}`} role="status">
            {err ?? msg}
          </div>
        )}

        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (mode === "signin") handleSignIn();
            if (mode === "signup") handleSignUp();
            if (mode === "reset") handleReset();
          }}
        >
          {mode === "signup" && (
            <label className="auth-field">
              <span>Nombre</span>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Tu nombre"
                required
                className="auth-input"
              />
            </label>
          )}

          <label className="auth-field">
            <span>Correo</span>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@correo.com"
              required
              className="auth-input"
            />
          </label>

          {mode !== "reset" && (
            <>
              <label className="auth-field">
                <span>Contraseña</span>
                <input
                  type="password"
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="auth-input"
                />
                <em className="auth-hint">Mínimo 6 caracteres.</em>
              </label>

              {mode === "signup" && (
                <label className="auth-field">
                  <span>Repite la contraseña</span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={password2}
                    onChange={(e) => setPassword2(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="auth-input"
                  />
                </label>
              )}
            </>
          )}

          <div className="auth-actions">
            {mode === "signin" && (
              <button
                type="submit"
                disabled={loading || !canSignin}
                className="btn btn-primary"
              >
                {loading ? "Cargando…" : "Entrar"}
              </button>
            )}

            {mode === "signup" && (
              <button
                type="submit"
                disabled={loading || !canSignup}
                className="btn btn-primary"
              >
                {loading ? "Cargando…" : "Crear cuenta"}
              </button>
            )}

            {mode === "reset" && (
              <button
                type="submit"
                disabled={loading || !isEmailOk}
                className="btn btn-primary"
              >
                {loading ? "Cargando…" : "Enviar enlace de restablecimiento"}
              </button>
            )}
          </div>
        </form>

        <div className="auth-links">
          {mode !== "signup" && (
            <button onClick={() => setMode("signup")} disabled={loading} className="btn btn-ghost">
              Crear cuenta nueva
            </button>
          )}
          {mode !== "signin" && (
            <button onClick={() => setMode("signin")} disabled={loading} className="btn btn-ghost">
              Ya tengo cuenta
            </button>
          )}
          {mode !== "reset" && (
            <button onClick={() => setMode("reset")} disabled={loading} className="btn btn-ghost">
              Olvidé mi contraseña
            </button>
          )}
        </div>

        <div className="auth-sep">
          <span>o entra con</span>
        </div>

        <div className="auth-oauth">
          <button
            onClick={() => handleOAuth("google")}
            disabled={loading}
            className="btn btn-oauth"
            aria-label="Entrar con Google"
            type="button"
          >
            <GoogleIcon />
            <span>Google</span>
          </button>
          <button
            onClick={() => handleOAuth("github")}
            disabled={loading}
            className="btn btn-oauth"
            aria-label="Entrar con GitHub"
            type="button"
          >
            <GitHubIcon />
            <span>GitHub</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/** Icono Google (SVG inline) */
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#FFC107"
        d="M43.61 20.083h-1.86V20H24v8h11.303C33.46 31.91 29.15 35 24 35c-6.627 0-12-5.373-12-12S17.373 11 24 11c3.06 0 5.84 1.153 7.957 3.043l5.657-5.657C33.563 5.04 28.973 3 24 3 12.955 3 4 11.955 4 23s8.955 20 20 20 20-8.955 20-20c0-1.34-.138-2.65-.39-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.206 16.406 18.736 13 24 13c3.06 0 5.84 1.153 7.957 3.043l5.657-5.657C33.563 5.04 28.973 3 24 3 16.318 3 9.72 7.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 43c5.09 0 9.676-1.92 13.17-5.06l-6.07-4.974C29.064 34.99 26.69 36 24 36c-5.12 0-9.44-3.09-11.24-7.46l-6.54 5.04C8.573 39.52 15.76 43 24 43z"
      />
      <path
        fill="#1976D2"
        d="M43.61 20.083H24v8h11.303c-1.255 3.63-4.51 6.27-8.303 6.27-5.12 0-9.44-3.09-11.24-7.46l-6.54 5.04C11.427 39.52 18.614 43 24 43c8.837 0 16-7.163 16-16 0-1.34-.138-2.65-.39-3.917z"
      />
    </svg>
  );
}

/** Icono GitHub (SVG inline) */
function GitHubIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M8 0C3.58 0 0 3.58 0 8a8 8 0 0 0 5.47 7.59c.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.2 1.87.86 2.33.65.07-.52.28-.86.51-1.06-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.31-.27 1.99-.27.68 0 1.35.09 1.99.27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.28.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8 8 0 0 0 16 8c0-4.42-3.58-8-8-8z"
      />
    </svg>
  );
}
